/**
 * ProPilote - Offline IndexedDB layer
 *
 * Stores 2 things:
 *  1. Pending mutations (POST/PUT/DELETE) when offline ou api-fail
 *  2. Read-only cache of GET responses (last 7 days for resilient navigation)
 *
 * Why IndexedDB over localStorage:
 *  - Async (non-blocking)
 *  - 50 MB+ vs 5 MB
 *  - Structured, indexed queries
 */

import { openDB } from 'idb';

const DB_NAME = 'propilote-offline';
const DB_VERSION = 2;

let _dbPromise = null;

const getDB = () => {
  if (!_dbPromise) {
    _dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        // V1 -> initial stores
        if (oldVersion < 1) {
          if (!db.objectStoreNames.contains('mutations')) {
            const m = db.createObjectStore('mutations', { keyPath: 'id', autoIncrement: true });
            m.createIndex('by_status', 'status');
            m.createIndex('by_created', 'created_at');
          }
          if (!db.objectStoreNames.contains('cache')) {
            db.createObjectStore('cache', { keyPath: 'key' });
          }
        }
        // V2 -> blob queue
        if (oldVersion < 2) {
          if (!db.objectStoreNames.contains('blobs')) {
            const b = db.createObjectStore('blobs', { keyPath: 'id', autoIncrement: true });
            b.createIndex('by_status', 'status');
            b.createIndex('by_stop', 'stop_id');
          }
        }
      },
    });
  }
  return _dbPromise;
};

// =========================================================================
// MUTATIONS QUEUE
// =========================================================================

/**
 * Push a failed/offline mutation to the queue.
 * Returns the assigned id.
 */
export const enqueueMutation = async ({ method, url, data = null, headers = {}, kind = 'generic' }) => {
  const db = await getDB();
  const item = {
    method: String(method).toUpperCase(),
    url,
    data,
    headers,
    kind, // ex: 'daily-report-create', 'stop-add', 'inspection-submit'
    status: 'pending', // pending | syncing | failed
    attempts: 0,
    last_error: null,
    created_at: Date.now(),
    updated_at: Date.now(),
  };
  return db.add('mutations', item);
};

export const listMutations = async (statusFilter = null) => {
  const db = await getDB();
  if (statusFilter) {
    return db.getAllFromIndex('mutations', 'by_status', statusFilter);
  }
  return db.getAll('mutations');
};

export const countPending = async () => {
  const db = await getDB();
  const tx = db.transaction('mutations');
  let count = 0;
  for await (const cursor of tx.store.index('by_status').iterate('pending')) {
    count += 1;
  }
  return count;
};

export const updateMutation = async (id, patch) => {
  const db = await getDB();
  const item = await db.get('mutations', id);
  if (!item) return null;
  Object.assign(item, patch, { updated_at: Date.now() });
  await db.put('mutations', item);
  return item;
};

export const deleteMutation = async (id) => {
  const db = await getDB();
  return db.delete('mutations', id);
};

export const clearMutations = async (statusFilter = null) => {
  const db = await getDB();
  if (!statusFilter) {
    return db.clear('mutations');
  }
  const tx = db.transaction('mutations', 'readwrite');
  for await (const cursor of tx.store.index('by_status').iterate(statusFilter)) {
    await cursor.delete();
  }
};

// =========================================================================
// READ CACHE
// =========================================================================

const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export const cachePut = async (key, data) => {
  const db = await getDB();
  return db.put('cache', { key, data, ts: Date.now() });
};

export const cacheGet = async (key, { maxAgeMs = CACHE_TTL_MS } = {}) => {
  const db = await getDB();
  const entry = await db.get('cache', key);
  if (!entry) return null;
  if (Date.now() - entry.ts > maxAgeMs) {
    await db.delete('cache', key);
    return null;
  }
  return entry.data;
};

export const cacheClear = async () => {
  const db = await getDB();
  return db.clear('cache');
};

// =========================================================================
// BLOB QUEUE (BOL scans + other binary uploads)
// =========================================================================

/**
 * Enqueue a binary file upload that failed because we're offline.
 * The Blob is stored AS-IS in IndexedDB (browsers support it natively).
 *
 * Params:
 *   - blob: Blob | File
 *   - filename: string
 *   - report_id, stop_id: linkage
 *   - extra_form: { enhance_mode, ... } extra form fields to send at replay
 *   - upload_url: relative URL to POST the FormData to (must be queueable)
 */
export const enqueueBlob = async ({ blob, filename, report_id, stop_id, extra_form = {}, upload_url }) => {
  const db = await getDB();
  return db.add('blobs', {
    blob,
    filename: filename || 'scan.jpg',
    mime: blob.type || 'image/jpeg',
    size: blob.size,
    report_id,
    stop_id,
    extra_form,
    upload_url,
    status: 'pending',
    attempts: 0,
    last_error: null,
    created_at: Date.now(),
    updated_at: Date.now(),
  });
};

export const listBlobs = async (statusFilter = null) => {
  const db = await getDB();
  if (statusFilter) {
    return db.getAllFromIndex('blobs', 'by_status', statusFilter);
  }
  return db.getAll('blobs');
};

export const countPendingBlobs = async () => {
  const db = await getDB();
  let count = 0;
  for await (const cursor of db.transaction('blobs').store.index('by_status').iterate('pending')) {
    count += 1;
  }
  return count;
};

export const updateBlob = async (id, patch) => {
  const db = await getDB();
  const item = await db.get('blobs', id);
  if (!item) return null;
  Object.assign(item, patch, { updated_at: Date.now() });
  await db.put('blobs', item);
  return item;
};

export const deleteBlob = async (id) => {
  const db = await getDB();
  return db.delete('blobs', id);
};

// =========================================================================
// HOUSEKEEPING — purge automatique pour éviter le bloat IndexedDB
// (cause principale du "démarrage app lent" quand des centaines de scans
//  BOL ou mutations 'completed' restent en base sans être nettoyés).
// =========================================================================

const PURGE_MUTATION_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000; // 14 jours
const PURGE_BLOB_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;      // 7 jours pour blobs uploadés

export const purgeOldEntries = async () => {
  try {
    const db = await getDB();
    const now = Date.now();
    let purgedMutations = 0;
    let purgedBlobs = 0;
    let purgedCache = 0;

    // Purge cache expiré
    const cacheKeys = await db.getAllKeys('cache');
    for (const key of cacheKeys) {
      const entry = await db.get('cache', key);
      if (entry && (now - (entry.ts || 0)) > CACHE_TTL_MS) {
        await db.delete('cache', key);
        purgedCache++;
      }
    }

    // Purge mutations 'completed' ou 'failed' anciennes
    const muts = await db.getAll('mutations');
    for (const m of muts) {
      const isOld = (now - (m.updated_at || m.created_at || 0)) > PURGE_MUTATION_MAX_AGE_MS;
      const isTerminal = m.status === 'completed' || (m.status === 'failed' && m.attempts >= 5);
      if (isTerminal && isOld) {
        await db.delete('mutations', m.id);
        purgedMutations++;
      }
    }

    // Purge blobs uploadés (status !== 'pending') anciens
    const blobs = await db.getAll('blobs');
    for (const b of blobs) {
      const isOld = (now - (b.updated_at || b.created_at || 0)) > PURGE_BLOB_MAX_AGE_MS;
      const isUploaded = b.status === 'uploaded' || b.status === 'completed';
      if (isUploaded && isOld) {
        await db.delete('blobs', b.id);
        purgedBlobs++;
      }
    }

    if (purgedMutations || purgedBlobs || purgedCache) {
      console.log(`[offlineDB] purge: ${purgedMutations} mutations, ${purgedBlobs} blobs, ${purgedCache} cache entries`);
    }
    return { purgedMutations, purgedBlobs, purgedCache };
  } catch (e) {
    console.warn('[offlineDB] purge error:', e);
    return { purgedMutations: 0, purgedBlobs: 0, purgedCache: 0 };
  }
};

export default {
  enqueueMutation,
  listMutations,
  countPending,
  updateMutation,
  deleteMutation,
  clearMutations,
  cachePut,
  cacheGet,
  cacheClear,
  enqueueBlob,
  listBlobs,
  countPendingBlobs,
  updateBlob,
  deleteBlob,
  purgeOldEntries,
};
